import React, { useCallback, useRef, useState } from "react";
import { Alert, FormHelperText, Typography, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Input from "@components/Input/Input";
import {
  DRAWER_TEXT_MAX_CHARS,
  getDrawerTextAllowedCharactersMessage,
  isDrawerTextAllowed,
  normalizeDrawerText,
} from "@components/SideDrawer/SideDrawer.schema";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { GatepassError } from "@features/gatepass/components/types.ts";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { bulkCompleteGatepass } from "@services/gatepassDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const UPLOAD_KEY_BULK_APPROVE = "gatepassBulkApproveAttachment";
const service = "bff-gatepass";
const TEXTAREA_MAX_CHARS = DRAWER_TEXT_MAX_CHARS;

export type BulkGatepassRow = {
  id: string;
  gatepassId: string;
  taskId?: string | null;
};

interface BulkApproveGatepassDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRows: BulkGatepassRow[];
  onSuccess?: () => void;
}

const BulkApproveGatepassDrawer: React.FC<BulkApproveGatepassDrawerProps> = ({
  isOpen,
  onClose,
  selectedRows,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const [notes, setNotes] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>();
  const [errors, setErrors] = useState<{ notes?: string; files?: string }>({});
  const [error, setError] = useState<null | string>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const attachmentFiles = useUploadStore(
    (state) => state.filesByKey[UPLOAD_KEY_BULK_APPROVE] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const uploadSignatureRef = useRef<string | null>(null);

  const setAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(UPLOAD_KEY_BULK_APPROVE);
        return;
      }
      setFilesForKey(UPLOAD_KEY_BULK_APPROVE, files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "BulkApproveGatepass",
          action: "upload_gatepass_document",
          entityType: "gatepass_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };
          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });
          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType };
        },
      ),
    onError: (error: Error) => setError(error.message),
  });

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.RefObject<string | null>,
    ) => {
      if (!handler || !(entry instanceof File)) return;
      const signature = getFileSignature(entry);
      if (!signature || tracker.current === signature) return;
      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const handleDocumentUpload = useCallback((file: File) => {
    documentUploadMutation.mutate(
      { file, documentType: "GATEPASS_ATTACHMENT" },
      {
        onSuccess: ({ documentUrl }: { documentUrl: string }) => {
          setError(null);
          setAttachmentUrl(documentUrl);
        },
      },
    );
  }, []);

  const handleFileUpload = useCallback(
    (newFiles: File[]) => {
      if (newFiles.length > 1) {
        setErrors((prev) => ({ ...prev, files: t("upload.one-document") }));
        return;
      }
      const file = newFiles[0];
      if (!file) return;
      setErrors((prev) => ({ ...prev, files: undefined }));
      setAttachmentFiles([file]);
      triggerUpload(file, handleDocumentUpload, uploadSignatureRef);
    },
    [handleDocumentUpload, setAttachmentFiles, t, triggerUpload],
  );

  const resetFormValues = useCallback(() => {
    setNotes("");
    setAttachmentUrl(undefined);
    setErrors({});
    setAttachmentFiles([]);
    setShowConfirmDialog(false);
    uploadSignatureRef.current = null;
  }, [setAttachmentFiles]);

  const handleCloseDrawer = useCallback(() => {
    onClose();
    resetFormValues();
  }, [onClose, resetFormValues]);

  const bulkCompleteMutation = useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: "gatepass",
          screen: "BulkApproveGatepass",
          action: "bulk_approve_gatepass",
          entityType: "gatepass",
        },
        async (transactionId) => {
          const ids = selectedRows
            .map((r) => r.taskId as string | null)
            .filter((taskId): taskId is string => Boolean(taskId));
          const taskDecision: {
            status: "APPROVED";
            reason?: string;
            notes?: string;
            validityPeriod?: number;
            attachments?: Array<{
              originalFileName: string;
              url: string;
              type: string;
            }>;
            autoApproval: boolean;
          } = {
            status: "APPROVED",
            autoApproval: false,
          };
          const trimmedNotes = normalizeDrawerText(notes);
          if (trimmedNotes) {
            taskDecision.reason = trimmedNotes;
            taskDecision.notes = trimmedNotes;
          }
          if (attachmentUrl) {
            const file = attachmentFiles[0];
            const originalFileName =
              file instanceof File ? file.name : (file as { name?: string })?.name || "attachment";
            taskDecision.attachments = [
              {
                originalFileName,
                url: attachmentUrl,
                type: "APPROVE_ATTACHMENTS",
              },
            ];
          }
          return bulkCompleteGatepass(
            service,
            { ids, taskDecision },
            ...(transactionId ? [transactionId] : []),
          );
        },
      ),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"], exact: false });

      onSuccess?.();
      onClose();
      resetFormValues();
      const successCount = data?.successfulCount ?? selectedRows.length;
      const failCount = data?.failedCount ?? 0;
      if (failCount > 0) {
        notify({
          title: t("gatepass.bulk-approve-partial", {
            defaultValue: "Bulk approve completed with errors",
          }),
          description: t("gatepass.bulk-approve-partial-detail", {
            defaultValue: "{{success}} approved, {{failed}} failed.",
            success: successCount,
            failed: failCount,
          }),
          type: "warning",
        });
      } else {
        notify({
          title: t("gatepass.bulk-approve-success", {
            defaultValue: "Gatepasses approved",
          }),
          type: "success",
        });
      }
    },
    onError: async (err: GatepassError) => {
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"], exact: false });
      setShowConfirmDialog(false);
      notify({
        title: t("common.error"),
        description:
          err?.response?.data?.detail ?? err?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
      if (err.status === 409) {
        handleCloseDrawer();
      }
    },
  });

  const validateNotes = useCallback(
    (value: string) => {
      if (value.length > TEXTAREA_MAX_CHARS) {
        return t("form-validation.max-length", {
          label: t("gatepass.notes"),
          max: TEXTAREA_MAX_CHARS,
        });
      }

      if (!isDrawerTextAllowed(value)) {
        return getDrawerTextAllowedCharactersMessage(t);
      }

      return undefined;
    },
    [t],
  );

  const handleSubmit = useCallback(() => {
    if (documentUploadMutation.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const trimmedNotes = normalizeDrawerText(notes);
    setNotes(trimmedNotes);

    const notesError = validateNotes(trimmedNotes);
    if (notesError) {
      setErrors((prev) => ({ ...prev, notes: notesError }));
      return;
    }

    setErrors({});
    setShowConfirmDialog(true);
  }, [documentUploadMutation.isPending, notes, notify, t, validateNotes]);

  const handleConfirmDialogConfirm = useCallback(() => {
    bulkCompleteMutation.mutate();
  }, [bulkCompleteMutation]);

  const handleConfirmDialogClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const inputSlotProps = {
    root: {
      className: "rounded-lg text-sm px-4 py-2 border",
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: { maxLength: TEXTAREA_MAX_CHARS },
  };

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": { padding: 0 },
  };

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  if (selectedRows.length === 0) return null;

  return (
    <div data-testid="bulk-approve-gatepass-drawer-form" className="max-h-screen overflow-y-auto">
      <SideDrawer
        isOpen={isOpen}
        title={t("gatepass.bulk-approve-drawer-title", { defaultValue: "Bulk Approve Gatepass" })}
        onClose={handleCloseDrawer}
        submitLabel={t("gatepass.bulk-approve-button", { defaultValue: "Bulk Approve" })}
        onSubmit={handleSubmit}
        isSubmitting={bulkCompleteMutation.isPending}
      >
        <div data-testid="bulk-approve-gatepass-drawer-content" className="flex-1 max-w-sm">
          <div className="px-6">
            <Typography
              variant="body1"
              data-testid="bulk-approve-gatepass-form-title"
              sx={{ color: "text.primary" }}
            >
              {t("gatepass.bulk-approve-drawer-message", {
                defaultValue: "You are about to bulk approve Gatepass",
              })}
            </Typography>
            <Typography component="div" variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              {selectedRows.map((row) => (
                <span key={row.id}>
                  #{row.gatepassId}
                  <br />
                </span>
              ))}
            </Typography>
            <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
              <CustomFormLabel>
                {t("gatepass.notes-will-apply-all", {
                  defaultValue: "Notes (will apply to all gatepasses)",
                })}
              </CustomFormLabel>
              <Input
                placeholder={t("gatepass.notes-placeholder", { defaultValue: "Enter notes" })}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                value={notes}
                onKeyDown={(e) => {
                  if (!isPrintableKey(e)) return;
                  if (notes.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      notes: t("form-validation.max-length", {
                        label: t("gatepass.notes"),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setNotes(value);
                  setErrors((prev) => ({ ...prev, notes: validateNotes(value) }));
                }}
                onBlur={(e) => {
                  const trimmed = normalizeDrawerText(e.target.value);
                  if (trimmed !== notes) setNotes(trimmed);
                  setErrors((prev) => ({ ...prev, notes: validateNotes(trimmed) }));
                }}
                slotProps={inputSlotProps}
                sx={sxProps}
                testId="bulk-approve-gatepass-notes-input"
              />
              {errors.notes && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {errors.notes}
                </p>
              )}
              <FormHelperText sx={charCounterSx}>
                {notes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
            </div>
            <div className="col-span-full mt-6">
              <div className={errors.files ? "border border-red-500 rounded-md p-1" : ""}>
                <DocumentUpload
                  label={t("gatepass.attachments", { defaultValue: "Attachments" })}
                  testId="bulk-approve-gatepass-attachment-upload"
                  name={UPLOAD_KEY_BULK_APPROVE}
                  isSideDrawer
                  maxFiles={1}
                  allowedExtensions={allowedExtensions}
                  onUpload={handleFileUpload}
                  conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                  files={attachmentFiles}
                  setFiles={setAttachmentFiles}
                  uploadDescription={t("upload.dragOrDropDescription")}
                  disabled={documentUploadMutation.isPending}
                />
              </div>
              {errors.files && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {errors.files}
                </p>
              )}
              {attachmentFiles[0] && !errors.files && (
                <div className="flex-1 mt-4">
                  <SingleFile
                    file={attachmentFiles[0]}
                    fieldName={UPLOAD_KEY_BULK_APPROVE}
                    setFiles={() => {
                      setErrors((prev) => ({ ...prev, files: undefined }));
                      setAttachmentFiles([]);
                      setAttachmentUrl(undefined);
                      uploadSignatureRef.current = null;
                    }}
                    isLoading={documentUploadMutation.isPending}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
      <GenericDialog
        open={showConfirmDialog}
        title={t("gatepass.bulk-approve-confirm-title", {
          defaultValue: "Confirm bulk approve",
        })}
        elementContent={
          <>
            {t("gatepass.bulk-approve-confirm-message", {
              defaultValue: "You are about to approve {{count}} gatepass request(s).",
              count: selectedRows.length,
            })}
            <br />
            {t("gatepass.approve-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm")}
        handleCloseDialog={handleConfirmDialogClose}
        confirmAction={handleConfirmDialogConfirm}
        testId="bulk-approve-gatepass-confirm-dialog"
        isInsideDrawer
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default BulkApproveGatepassDrawer;
